import { buildTileLoggingData } from "@faire/retailer-visitor-shared/components/SDUI/__internal__/EventLogging/buildLoggingData";
import { ComponentLoggingEventParams } from "@faire/retailer-visitor-shared/components/SDUI/__internal__/types";
import { SDUIProductTileRenderer } from "@faire/retailer-visitor-shared/components/SDUI/ComponentRenderers/Tile/__internal__/ProductTile/SDUIProductTileRenderer";
import { SDUIProductTileWithBrand } from "@faire/retailer-visitor-shared/components/SDUI/ComponentRenderers/Tile/__internal__/ProductTile/SDUIProductTileWithBrandRenderer";
import { SDUIAvatarTileRenderer } from "@faire/retailer-visitor-shared/components/SDUI/ComponentRenderers/Tile/__internal__/SDUIAvatarTileRenderer";
import { SDUIBrandTileRenderer } from "@faire/retailer-visitor-shared/components/SDUI/ComponentRenderers/Tile/__internal__/SDUIBrandTileRenderer";
import { SDUIBrandTileWithActionRenderer } from "@faire/retailer-visitor-shared/components/SDUI/ComponentRenderers/Tile/__internal__/SDUIBrandTileWithActionRenderer";
import { SDUIImageGalleryTileRenderer } from "@faire/retailer-visitor-shared/components/SDUI/ComponentRenderers/Tile/__internal__/SDUIImageGalleryTileRenderer";
import { SDUIImageTileRenderer } from "@faire/retailer-visitor-shared/components/SDUI/ComponentRenderers/Tile/__internal__/SDUIImageTileRenderer";
import { SDUIPillTileRenderer } from "@faire/retailer-visitor-shared/components/SDUI/ComponentRenderers/Tile/__internal__/SDUIPillTileRenderer";
import { SDUITextTileRenderer } from "@faire/retailer-visitor-shared/components/SDUI/ComponentRenderers/Tile/__internal__/SDUITextTileRenderer";
import { SDUITileInstanceRendererProps } from "@faire/retailer-visitor-shared/components/SDUI/ComponentRenderers/Tile/__internal__/types";
import { ITile } from "@faire/web-api--source/indigofair/data/ITile";
import React, { useMemo } from "react";

import { SDUIAvatarInfoCardTileRenderer } from "./__internal__/SDUIAvatarInfoCardTile";
import { SDUIBrandInsightsTileRenderer } from "./__internal__/SDUIBrandInsightsTileRenderer";
import { SDUIButtonTileRenderer } from "./__internal__/SDUIButtonTileRenderer";
import { SDUIImageFanTileRenderer } from "./__internal__/SDUIImageFanTileRenderer";

export const SDUITileRenderer = ({
  tileElement,
  loggingData,
}: {
  tileElement: ITile;
  loggingData: ComponentLoggingEventParams;
  className?: string;
  style?: React.CSSProperties;
} & Partial<Pick<HTMLDivElement, "ariaHidden">>) => {
  const TileComponent = getTileComponent(tileElement);
  const { logging_data } = tileElement;
  const tileLoggingData = useMemo(
    () => buildTileLoggingData(loggingData, logging_data),
    [loggingData, logging_data]
  );

  if (TileComponent === undefined) {
    return null;
  }

  return (
    <TileComponent tileElement={tileElement} loggingData={tileLoggingData} />
  );
};

enum TileType {
  BRAND_TILE,
  BRAND_TILE_WITH_PRODUCTS,
  BRAND_TILE_WITH_ACTION,
  PRODUCTS_WITH_BRAND_INFO_TILE,
  PRODUCT_TILE,
  IMAGE_TILE,
  PILL_TILE,
  IMAGE_GALLERY_TILE,
  TEXT_TILE,
  AVATAR_TILE,
  BRAND_INSIGHTS_TILE,
  BUTTON_TILE,
  IMAGE_FAN_TILE,
  AVATAR_INFO_CARD_TILE,
}

const tileRendererMap: Partial<
  Record<TileType, React.FC<SDUITileInstanceRendererProps> | undefined>
> = {
  [TileType.BRAND_TILE]: SDUIBrandTileRenderer,
  [TileType.BRAND_TILE_WITH_ACTION]: SDUIBrandTileWithActionRenderer,
  [TileType.PRODUCTS_WITH_BRAND_INFO_TILE]: SDUIProductTileWithBrand,
  [TileType.PRODUCT_TILE]: SDUIProductTileRenderer,
  [TileType.IMAGE_TILE]: SDUIImageTileRenderer,
  [TileType.PILL_TILE]: SDUIPillTileRenderer,
  [TileType.IMAGE_GALLERY_TILE]: SDUIImageGalleryTileRenderer,
  [TileType.TEXT_TILE]: SDUITextTileRenderer,
  [TileType.AVATAR_TILE]: SDUIAvatarTileRenderer,
  [TileType.BRAND_INSIGHTS_TILE]: SDUIBrandInsightsTileRenderer,
  [TileType.BUTTON_TILE]: SDUIButtonTileRenderer,
  [TileType.IMAGE_FAN_TILE]: SDUIImageFanTileRenderer,
  [TileType.AVATAR_INFO_CARD_TILE]: SDUIAvatarInfoCardTileRenderer,
};

const getTileComponent = (
  tileElement: ITile
): React.FC<SDUITileInstanceRendererProps> | undefined => {
  if (tileElement.brand_tile) {
    return tileRendererMap[TileType.BRAND_TILE];
  } else if (tileElement.brand_tile_with_action) {
    return tileRendererMap[TileType.BRAND_TILE_WITH_ACTION];
  } else if (tileElement.product_tile) {
    return tileRendererMap[TileType.PRODUCT_TILE];
  } else if (tileElement.product_with_brand_info_tile) {
    return tileRendererMap[TileType.PRODUCTS_WITH_BRAND_INFO_TILE];
  } else if (tileElement.image_tile) {
    return tileRendererMap[TileType.IMAGE_TILE];
  } else if (tileElement.pill_tile) {
    return tileRendererMap[TileType.PILL_TILE];
  } else if (tileElement.image_gallery_tile) {
    return tileRendererMap[TileType.IMAGE_GALLERY_TILE];
  } else if (tileElement.text_tile) {
    return tileRendererMap[TileType.TEXT_TILE];
  } else if (tileElement.avatar_tile) {
    return tileRendererMap[TileType.AVATAR_TILE];
  } else if (tileElement.brand_insights_tile) {
    return tileRendererMap[TileType.BRAND_INSIGHTS_TILE];
  } else if (tileElement.button_tile) {
    return tileRendererMap[TileType.BUTTON_TILE];
  } else if (tileElement.image_fan_tile) {
    return tileRendererMap[TileType.IMAGE_FAN_TILE];
  } else if (tileElement.avatar_info_card_tile) {
    return tileRendererMap[TileType.AVATAR_INFO_CARD_TILE];
  }
  return undefined;
};
